import React, { useState, useMemo } from 'react';
import {
  Container,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Fade,
  Stack,
  alpha,
  Tooltip,
  Pagination,
  TableSortLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Inventory2Outlined as InventoryIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import inventoryService from '../services/inventoryService';

const InventoryDashboard = () => {
  const [searchInput, setSearchInput] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 50; // Show 50 rows per page on both mobile and PC

  // Sorting state
  const [orderBy, setOrderBy] = useState('Item Description');
  const [order, setOrder] = useState('asc');

  // Progressive loading state
  const [allInventoryData, setAllInventoryData] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0, page: 0 });
  const [isLoadingChunks, setIsLoadingChunks] = useState(true);
  const [isCached, setIsCached] = useState(false);
  const [cacheAge, setCacheAge] = useState(0);

  // Fetch data progressively in chunks — re-runs when period changes
  React.useEffect(() => {
    const fetchAllDataInChunks = async () => {
      try {
        setIsLoadingChunks(true);
        setAllInventoryData([]);
        setLoadingProgress({ loaded: 0, total: 0, page: 0 });
        let allData = [];
        let page = 1;
        const pageSize = 1000;

        while (true) {
          console.log(`📦 Fetching chunk ${page}...`);
          const response = await inventoryService.getInventory({ page, page_size: pageSize });

          allData = [...allData, ...response.data];

          setLoadingProgress({
            loaded: allData.length,
            total: response.total_count,
            page: page,
            totalPages: response.total_pages
          });

          setAllInventoryData([...allData]);
          setIsCached(response.cached);
          setCacheAge(response.cache_age_seconds || 0);

          console.log(`✅ Loaded ${allData.length}/${response.total_count} items (page ${page}/${response.total_pages})`);

          if (page >= response.total_pages) {
            console.log('🎉 All data loaded!');
            break;
          }

          page++;
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        setIsLoadingChunks(false);
      } catch (error) {
        console.error('Failed to load inventory data:', error);
        setIsLoadingChunks(false);
      }
    };

    fetchAllDataInChunks();
  }, []); // Run once on mount

  // Refresh function to reload all data
  const refetch = () => {
    setAllInventoryData([]);
    setLoadingProgress({ loaded: 0, total: 0, page: 0 });
    setIsLoadingChunks(true);

    const fetchAllDataInChunks = async () => {
      try {
        let allData = [];
        let page = 1;
        const pageSize = 1000; // Fetch 1000 items per chunk for better performance

        while (true) {
          const response = await inventoryService.getInventory({ page, page_size: pageSize });
          allData = [...allData, ...response.data];

          setLoadingProgress({
            loaded: allData.length,
            total: response.total_count,
            page: page,
            totalPages: response.total_pages
          });

          setAllInventoryData([...allData]);
          setIsCached(response.cached);
          setCacheAge(response.cache_age_seconds || 0);

          if (page >= response.total_pages) break;
          page++;
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        setIsLoadingChunks(false);
      } catch (error) {
        console.error('Failed to refresh inventory data:', error);
        setIsLoadingChunks(false);
      }
    };

    fetchAllDataInChunks();
  };

  // Export all filtered+sorted data to Excel (not just current page)
  const exportToExcel = () => {
    const exportData = sortedData.map(item => ({
      'Item No': item['Item No'],
      'Item Description': item['Item Description'],
      'Store No': item['Store No'],
      'Store Description': item['Store Description'],
      'On Hand Quantity': item['On Hand Quantity'],
      'Inventory Quantity': item['Inventory Quantity'],
      'Sold Quantity': item['Sold Quantity'],
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
    XLSX.writeFile(wb, `inventory-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Handle sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // CLIENT-SIDE filtering - INSTANT, no API calls, no page refresh
  const filteredData = useMemo(() => {
    let filtered = allInventoryData;

    // Filter by search term (case-insensitive, instant) - includes Item No even though column is hidden
    if (searchInput.trim()) {
      const searchLower = searchInput.toLowerCase();
      filtered = filtered.filter(item =>
        item['Item No']?.toLowerCase().includes(searchLower) ||
        item['Item Description']?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by branch
    if (selectedBranch) {
      filtered = filtered.filter(item => item['Store No'] === selectedBranch);
    }

    // Filter by status
    if (statusFilter === 'in_stock') {
      filtered = filtered.filter(item => item['On Hand Quantity'] >= 10);
    } else if (statusFilter === 'low_stock') {
      filtered = filtered.filter(item => item['On Hand Quantity'] > 0 && item['On Hand Quantity'] < 10);
    } else if (statusFilter === 'out_of_stock') {
      filtered = filtered.filter(item => item['On Hand Quantity'] <= 0);
    }

    return filtered;
  }, [allInventoryData, searchInput, selectedBranch, statusFilter]);

  // CLIENT-SIDE sorting
  const sortedData = useMemo(() => {
    const sorted = [...filteredData];
    sorted.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle numeric values
      if (orderBy === 'On Hand Quantity' || orderBy === 'Sold Quantity' || orderBy === 'Inventory Quantity') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else {
        // Handle string values
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
    return sorted;
  }, [filteredData, orderBy, order]);

  // Memoized branches extraction from all data
  const branches = useMemo(() => {
    const branchMap = new Map();
    allInventoryData.forEach(item => {
      const code = item['Store No'];
      const description = item['Store Description'];
      if (code && !branchMap.has(code)) {
        branchMap.set(code, { code, description: description || code });
      }
    });

    return Array.from(branchMap.values()).sort((a, b) =>
      a.description.localeCompare(b.description)
    );
  }, [allInventoryData]);

  // Calculate stats from FILTERED data (memoized to prevent recalculation)
  const stats = useMemo(() => ({
    total: filteredData.length,
    lowStock: filteredData.filter(item => item['On Hand Quantity'] > 0 && item['On Hand Quantity'] < 10).length,
    outOfStock: filteredData.filter(item => item['On Hand Quantity'] <= 0).length,
    inStock: filteredData.filter(item => item['On Hand Quantity'] >= 10).length
  }), [filteredData]);

  // Pagination logic - slice sorted data for current page
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchInput, selectedBranch, statusFilter]);

  // Get stock status color
  const getStockColor = (quantity) => {
    if (quantity <= 0) return 'error';
    if (quantity < 10) return 'warning';
    return 'success';
  };

  // Get stock status label
  const getStockLabel = (quantity) => {
    if (quantity <= 0) return 'Out of Stock';
    if (quantity < 10) return 'Low Stock';
    return 'In Stock';
  };

  // Show loading screen only if no data loaded yet
  if (isLoadingChunks && allInventoryData.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
      >
        <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
        <Typography variant="h6" color="white">Loading inventory data...</Typography>
        <Typography variant="body2" color="white" sx={{ mt: 1, opacity: 0.8 }}>
          Initializing...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      pb: 6
    }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {/* Header */}
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <InventoryIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h4" component="h1" fontWeight="700" color="text.primary">
                    Inventory Dashboard
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {allInventoryData.length} items loaded • Client-side instant filtering
                    </Typography>
                    {isLoadingChunks && (
                      <Tooltip title={`Loading chunk ${loadingProgress.page}/${loadingProgress.totalPages}`}>
                        <Chip
                          icon={<CircularProgress size={12} />}
                          label={`Loading ${Math.round((loadingProgress.loaded / loadingProgress.total) * 100)}%`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {!isLoadingChunks && isCached && (
                      <Tooltip title={`Backend cached (${Math.round(cacheAge)}s old) • Instant client-side search`}>
                        <Chip
                          icon={<SpeedIcon />}
                          label="Complete"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Tooltip title={`Export ${sortedData.length.toLocaleString()} rows to Excel`}>
                  <span>
                    <IconButton
                      onClick={exportToExcel}
                      size="large"
                      disabled={isLoadingChunks || sortedData.length === 0}
                      sx={{
                        background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
                        color: 'white',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1b5e20 0%, #43a047 100%)',
                        },
                        '&:disabled': {
                          background: 'rgba(67, 160, 71, 0.4)',
                          color: 'rgba(255, 255, 255, 0.5)'
                        }
                      }}
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Refresh all data from server">
                  <IconButton
                    onClick={refetch}
                    size="large"
                    disabled={isLoadingChunks}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        transform: 'rotate(180deg)',
                        transition: 'transform 0.6s ease'
                      },
                      '&:disabled': {
                        background: 'rgba(102, 126, 234, 0.5)',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Paper>
        </Fade>

        {/* Progressive Loading Info */}
        {isLoadingChunks && allInventoryData.length > 0 && (
          <Fade in>
            <Alert
              severity="info"
              sx={{ mb: 3, borderRadius: 2 }}
              icon={<CircularProgress size={20} />}
            >
              Loading data in chunks: {loadingProgress.loaded.toLocaleString()} / {loadingProgress.total.toLocaleString()} items loaded (Page {loadingProgress.page}/{loadingProgress.totalPages})
            </Alert>
          </Fade>
        )}

        {/* Summary Stats */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Fade in timeout={1000}>
              <Card
                elevation={0}
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        background: alpha('#667eea', 0.1),
                        borderRadius: 2,
                        p: 1.5
                      }}
                    >
                      <InventoryIcon sx={{ fontSize: 28, color: '#667eea' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="500">
                        {searchInput || selectedBranch ? 'Filtered' : 'Total Items'}
                      </Typography>
                      <Typography variant="h4" fontWeight="700" color="text.primary">
                        {stats.total}
                      </Typography>
                      {(searchInput || selectedBranch) && (
                        <Typography variant="caption" color="text.disabled">
                          of {allInventoryData.length}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Fade in timeout={1200}>
              <Card
                elevation={0}
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        background: alpha('#4caf50', 0.1),
                        borderRadius: 2,
                        p: 1.5
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 28, color: '#4caf50' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="500">
                        In Stock
                      </Typography>
                      <Typography variant="h4" fontWeight="700" color="#4caf50">
                        {stats.inStock}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Fade in timeout={1400}>
              <Card
                elevation={0}
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        background: alpha('#ff9800', 0.1),
                        borderRadius: 2,
                        p: 1.5
                      }}
                    >
                      <WarningIcon sx={{ fontSize: 28, color: '#ff9800' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="500">
                        Low Stock
                      </Typography>
                      <Typography variant="h4" fontWeight="700" color="#ff9800">
                        {stats.lowStock}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Fade in timeout={1600}>
              <Card
                elevation={0}
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        background: alpha('#f44336', 0.1),
                        borderRadius: 2,
                        p: 1.5
                      }}
                    >
                      <TrendingDownIcon sx={{ fontSize: 28, color: '#f44336' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="500">
                        Out of Stock
                      </Typography>
                      <Typography variant="h4" fontWeight="700" color="#f44336">
                        {stats.outOfStock}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        </Grid>

        {/* Filters */}
        <Fade in timeout={1800}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search Items"
                  placeholder="Instant search - type to filter items (supports Arabic & English)..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' }
                    }
                  }}
                >
                  <InputLabel>Filter by Branch</InputLabel>
                  <Select
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    label="Filter by Branch"
                  >
                    <MenuItem value="">All Branches</MenuItem>
                    {branches.map((branch) => (
                      <MenuItem key={branch.code} value={branch.code}>
                        {branch.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' }
                    }
                  }}
                >
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Filter by Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="in_stock">In Stock</MenuItem>
                    <MenuItem value="low_stock">Low Stock</MenuItem>
                    <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* Data Table */}
        <Fade in timeout={2000}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {/* Item No column removed from view but still searchable */}
                  <TableCell
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.95rem'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'Item Description'}
                      direction={orderBy === 'Item Description' ? order : 'asc'}
                      onClick={() => handleSort('Item Description')}
                      sx={{
                        color: 'white !important',
                        '&:hover': { color: 'white !important' },
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Description
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.95rem'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'Store No'}
                      direction={orderBy === 'Store No' ? order : 'asc'}
                      onClick={() => handleSort('Store No')}
                      sx={{
                        color: 'white !important',
                        '&:hover': { color: 'white !important' },
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Store
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.95rem'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'Sold Quantity'}
                      direction={orderBy === 'Sold Quantity' ? order : 'asc'}
                      onClick={() => handleSort('Sold Quantity')}
                      sx={{
                        color: 'white !important',
                        '&:hover': { color: 'white !important' },
                        '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        '& .MuiTableSortLabel-root': { flexDirection: 'row-reverse' }
                      }}
                    >
                      Sold
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.95rem'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'Inventory Quantity'}
                      direction={orderBy === 'Inventory Quantity' ? order : 'asc'}
                      onClick={() => handleSort('Inventory Quantity')}
                      sx={{
                        color: 'white !important',
                        '&:hover': { color: 'white !important' },
                        '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        '& .MuiTableSortLabel-root': { flexDirection: 'row-reverse' }
                      }}
                    >
                      Inventory Qty
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.95rem'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'On Hand Quantity'}
                      direction={orderBy === 'On Hand Quantity' ? order : 'asc'}
                      onClick={() => handleSort('On Hand Quantity')}
                      sx={{
                        color: 'white !important',
                        '&:hover': { color: 'white !important' },
                        '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        '& .MuiTableSortLabel-root': { flexDirection: 'row-reverse' }
                      }}
                    >
                      On Hand
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.95rem'
                    }}
                  >
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box py={6}>
                        <InventoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          {allInventoryData.length === 0 ? 'No inventory data available' : 'No matching items found'}
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          {allInventoryData.length === 0
                            ? 'Try refreshing the data'
                            : 'Try adjusting your search or filters'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: alpha('#667eea', 0.05)
                        },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      {/* Item No column removed - but still searchable */}
                      <TableCell>
                        <Typography variant="body2">
                          {item['Item Description']}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item['Item No']}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {item['Store No']}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item['Store Description']}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {Math.abs(item['Sold Quantity'])}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item['Inventory Quantity']}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="700"
                          sx={{
                            color: item['On Hand Quantity'] <= 0
                              ? '#f44336'
                              : item['On Hand Quantity'] < 10
                                ? '#ff9800'
                                : '#4caf50'
                          }}
                        >
                          {item['On Hand Quantity']}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStockLabel(item['On Hand Quantity'])}
                          color={getStockColor(item['On Hand Quantity'])}
                          size="small"
                          sx={{
                            fontWeight: '600',
                            borderRadius: 1.5
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Fade>

        {/* Pagination Controls */}
        {sortedData.length > itemsPerPage && (
          <Fade in timeout={2200}>
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              >
                <Stack direction="row" spacing={3} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} items
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, page) => setCurrentPage(page)}
                    color="primary"
                    shape="rounded"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: 2,
                        fontWeight: 600,
                        '&.Mui-selected': {
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                          }
                        }
                      }
                    }}
                  />
                </Stack>
              </Paper>
            </Box>
          </Fade>
        )}

      </Container>
    </Box>
  );
};

export default InventoryDashboard;
